import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Heart, ImageOff, ChevronLeft, ChevronRight, Flame, Zap } from "lucide-react";
import api from "@/lib/api";
import { BADGES } from "@/lib/amenities";

const resolvePhoto = (p) => {
  if (!p) return null;
  if (p.startsWith("http")) return p;
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
};

export default function ListingCard({ listing, onFavoriteChange, isFavorite = false, onWishlistClick }) {
  const photos = (listing.photos || []).map(resolvePhoto).filter(Boolean);
  const [idx, setIdx] = useState(0);

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onWishlistClick) {
      onWishlistClick(listing);
      return;
    }
    try {
      const { data } = await api.post(`/favorites/${listing.id}`);
      onFavoriteChange?.(listing.id, data.favorited);
    } catch (_) {}
  };

  const nav = (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    if (!photos.length) return;
    setIdx((i) => (i + d + photos.length) % photos.length);
  };

  const trust = listing.trust || {};

  return (
    <Link
      to={`/listings/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group block"
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-[#F5F2EC]">
        {photos.length === 0 ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#F5F2EC] to-[#E5E1D8] text-neutral-500">
            <ImageOff className="w-8 h-8 mb-2 opacity-60" />
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Photos à venir</div>
          </div>
        ) : (
          <>
            {photos.map((p, i) => (
              <img
                key={i}
                src={p}
                alt={listing.title}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${i === idx ? "opacity-100" : "opacity-0"}`}
              />
            ))}
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => nav(e, -1)}
                  data-testid={`card-photo-prev-${listing.id}`}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity ${idx === 0 ? "pointer-events-none !opacity-0" : ""}`}
                  aria-label="Photo précédente"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => nav(e, 1)}
                  data-testid={`card-photo-next-${listing.id}`}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity ${idx === photos.length - 1 ? "pointer-events-none !opacity-0" : ""}`}
                  aria-label="Photo suivante"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                  {photos.slice(0, 5).map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === Math.min(idx, 4) ? "bg-white w-3" : "bg-white/60"}`} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Favorite button */}
        <button
          data-testid={`fav-btn-${listing.id}`}
          onClick={handleFav}
          className="absolute top-3 right-3 p-2 bg-white/95 hover:bg-white transition-colors rounded-full shadow"
          aria-label="Favori"
        >
          <Heart className={`w-4 h-4 ${isFavorite ? "fill-[#7A1535] text-[#7A1535]" : "text-[#0A0A0A]"}`} />
        </button>

        {/* Region tag */}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/85 text-white text-[10px] uppercase tracking-[0.18em] font-bold">
          {listing.region}
        </div>

        {/* Badges row */}
        {Array.isArray(listing.badges) && listing.badges.length > 0 && (
          <div className="absolute top-12 left-3 flex flex-wrap gap-1.5 max-w-[80%]">
            {listing.badges.slice(0, 2).map((b) => BADGES[b] && (
              <span
                key={b}
                data-testid={`badge-${b}-${listing.id}`}
                className={`px-2 py-1 text-[9px] uppercase tracking-[0.18em] font-bold ${BADGES[b].className}`}
              >
                {BADGES[b].label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="pt-4 pb-2">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base sm:text-lg font-semibold leading-snug text-neutral-900 group-hover:text-[#1B2A49] transition-colors line-clamp-2">
            {listing.title}
          </h3>
          {listing.avg_rating && (
            <div className="flex items-center gap-1 text-sm font-semibold shrink-0">
              <Star className="w-3.5 h-3.5 fill-[#0A0A0A] text-[#0A0A0A]" />
              {listing.avg_rating}
              {listing.review_count ? <span className="text-neutral-500 text-xs font-normal">({listing.review_count})</span> : null}
            </div>
          )}
        </div>
        <div className="mt-1 flex items-center text-xs text-neutral-500 gap-1">
          <MapPin className="w-3 h-3" />
          {listing.city}
        </div>

        {/* Trust signals */}
        {(trust.is_hot || trust.fast_response_min) && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {trust.is_hot && (
              <span
                data-testid={`trust-hot-${listing.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold text-[#9A4D1F]"
              >
                <Flame className="w-3 h-3" /> Réservé {trust.bookings_last_7d}× cette semaine
              </span>
            )}
            {trust.fast_response_min && (
              <span
                data-testid={`trust-fast-${listing.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold text-[#166534]"
              >
                <Zap className="w-3 h-3" /> Hôte répond en &lt; {trust.fast_response_min}min
              </span>
            )}
          </div>
        )}

        <div className="mt-2 text-sm">
          <span className="font-bold">{listing.price_per_night}€</span>
          <span className="text-neutral-500"> / nuit · {listing.max_guests} voyageurs</span>
        </div>
      </div>
    </Link>
  );
}
